import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocalizedName } from '../../utils/localization';
import { 
  Search, 
  Filter, 
  X, 
  ChevronDown, 
  Star, 
  MapPin, 
  Users,
  Building2,
  Award,
  CheckCircle,
  Globe,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  Shield,
  GraduationCap,
  Loader2,
  FilterX,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Eye,
  Heart,
  Share2,
  ExternalLink
} from 'lucide-react';
import { trainingCentersService, TrainingCenter, TrainingCenterFilters } from '../../services/training-centers.service';
import countriesService from '../../services/countries.service';
import { API_ENDPOINTS } from '../../config/api';

// Mock data for training centers (fallback)


// These will be loaded dynamically from the API

const TrainingCentersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { getLocalizedName } = useLocalizedName();
  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState<TrainingCenter[]>([]);
  const [filteredCenters, setFilteredCenters] = useState<TrainingCenter[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedWilaya, setSelectedWilaya] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);
  const [showPremiumOnly, setShowPremiumOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'rating' | 'name' | 'students' | 'trainings'>('rating');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);
  const [wilayas, setWilayas] = useState<{ id: number; name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: number; name_french?: string; name_english?: string; name?: string }[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Load static data (countries, categories) only once on mount
  useEffect(() => {
    const loadStaticData = async () => {
      try {
        const [countriesData, categoriesData] = await Promise.all([
          countriesService.getCountries(),
          trainingCentersService.getCategories(),
        ]);

        setCountries(countriesData);
        setCategories(categoriesData.map((category: any) => ({
          id: category.id,
          name_french: category.name_french,
          name_english: category.name_english,
          name: category.name
        })));
      } catch (error) {
      }
    };

    loadStaticData();
  }, []);

  // Load wilayas when country changes
  const loadWilayasForCountry = async (countryId: number) => {
    try {
      const wilayasData = await countriesService.getWilayas(countryId);
      setWilayas(wilayasData);
    } catch (error) {
      setWilayas([]);
    }
  };

  // Effect to load wilayas when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadWilayasForCountry(selectedCountry);
    } else {
      setWilayas([]);
    }
  }, [selectedCountry]);

  // Load training centers only (optimized - doesn't reload static data)
  const loadCenters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load training centers with current filters
      const filters: TrainingCenterFilters = {
        search: searchTerm || undefined,
        country: selectedCountry || undefined,
        wilaya: selectedCountry && wilayas.length > 0 && selectedWilaya ? selectedWilaya : undefined,
        specialty: selectedSpecialty || undefined,
        verified: showVerifiedOnly ? 'true' : undefined,
        premium: showPremiumOnly ? 'true' : undefined,
        minRating: minRating > 0 ? minRating : undefined,
        sortBy: sortBy,
        page: pagination.page,
        limit: pagination.limit,
      };

      const response = await trainingCentersService.getAllTrainingCenters(filters);
      
      setCenters(response.centers);
      setFilteredCenters(response.centers);
      setPagination(response.pagination);
    } catch (error) {
      setError(t('common.error'));
      // Fallback to empty array
      setCenters([]);
      setFilteredCenters([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCountry, selectedWilaya, selectedSpecialty, minRating, showVerifiedOnly, showPremiumOnly, sortBy, pagination.page, pagination.limit, wilayas.length, t]);

  // Load training centers when filters change (with debounce for search)
  // Reset to page 1 when filters change (except page itself)
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchTerm, selectedCountry, selectedWilaya, selectedSpecialty, minRating, showVerifiedOnly, showPremiumOnly, sortBy]);

  // Load centers when filters or pagination change (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadCenters();
    }, searchTerm ? 500 : 0); // Debounce search by 500ms

    return () => clearTimeout(timeoutId);
  }, [loadCenters, searchTerm, pagination.page]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCountry(null);
    setSelectedWilaya('');
    setSelectedSpecialty('');
    setMinRating(0);
    setShowVerifiedOnly(false);
    setShowPremiumOnly(false);
    setSortBy('rating');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedCountry) count++;
    if (selectedWilaya) count++;
    if (selectedSpecialty) count++;
    if (minRating > 0) count++;
    if (showVerifiedOnly) count++;
    if (showPremiumOnly) count++;
    return count;
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const TrainingCenterItem = ({ center, index }: { center: TrainingCenter; index: number }) => {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        className="group relative bg-white rounded-2xl border border-gray-200 hover:border-brand-primary/30 hover:shadow-xl hover:shadow-brand-primary/5 transition-all duration-300 overflow-hidden"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/3 via-transparent to-brand-secondary/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="relative p-4 sm:p-6 lg:p-8">
          {/* Header Row */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6 gap-4">
            <div className="flex items-center sm:items-start gap-4 sm:gap-6">
              {/* Logo */}
              <div className="relative flex-shrink-0">
                {center.logo ? (
                  <img
                    src={center.logo.startsWith('http') ? center.logo : `${API_ENDPOINTS.UPLOADS}/${center.logo}`}
                    alt={`${center.name} Logo`}
                    className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 rounded-xl object-cover bg-white shadow-md"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center shadow-md hidden`}>
                  <Building2 className="h-7 w-7 sm:h-8 sm:w-8 lg:h-10 lg:w-10 text-white" />
                </div>
                
                {/* Status Badges */}
                <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 flex flex-col gap-1">
                  {center.isVerified && (
                    <div className="bg-green-500 text-white rounded-full p-1">
                      <Shield className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    </div>
                  )}
                  {center.isPremium && (
                    <div className="bg-gradient-to-r from-brand-accent to-brand-secondary text-white rounded-full p-1">
                      <Star className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    </div>
                  )}
                </div>
              </div>

              {/* Center Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 group-hover:text-brand-primary transition-colors line-clamp-2">
                  {center.name}
                </h3>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-gray-600 mb-2 sm:mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-brand-primary" />
                    <span className="font-medium text-sm sm:text-base truncate">
                      {(() => {
                        const countryName = center.country?.name || '';
                        const wilayaName = center.wilaya?.name || '';
                        const city = center.city || '';
                        
                        if (!countryName) return 'Lieu non spécifié';
                        
                        if (countryName.toLowerCase() === 'algeria' || countryName.toLowerCase() === 'algérie') {
                          return wilayaName ? `${wilayaName}, ${countryName}` : countryName;
                        } else {
                          return city ? `${city}, ${countryName}` : countryName;
                        }
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-brand-secondary" />
                    <span className="text-sm sm:text-base whitespace-nowrap">Depuis {center.establishedYear}</span>
                  </div>
                </div>
                
                {/* Rating */}
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    {renderStars(center.rating)}
                  </div>
                  <span className="text-base sm:text-lg font-bold text-gray-900">{center.rating.toFixed(1)}</span>
                  <span className="text-gray-500 text-sm sm:text-base hidden sm:inline">({center.reviewCount} avis)</span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={() => navigate(`/training-center/${center.id}`)}
              className="w-full sm:w-auto px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-brand-primary to-brand-secondary text-white text-xs sm:text-sm font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-brand-primary/20 hover:scale-[1.02] whitespace-nowrap flex items-center justify-center gap-1.5 sm:gap-2"
            >
              <span className="hidden sm:inline">{t('public.trainingCenters.viewCenter')}</span>
              <span className="sm:hidden">Voir</span>
              <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>

          {/* Description */}
          <p className="text-gray-700 text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-2">
            {center.description}
          </p>

          {/* Bottom Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            {/* Specialties */}
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <span className="font-semibold text-gray-900">Spécialités:</span>
              <div className="flex flex-wrap gap-1.5">
                {center.specialties.slice(0, 3).map((specialty, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-brand-primary/10 text-brand-primary text-xs sm:text-sm font-medium rounded-full"
                  >
                    {specialty}
                  </span>
                ))}
                {center.specialties.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs sm:text-sm font-medium rounded-full">
                    +{center.specialties.length - 3}
                  </span>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-2 sm:gap-4 lg:gap-8">
              <div className="text-center">
                <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-brand-primary">{center.totalTrainings}</div>
                <div className="text-xs text-gray-600">Formations</div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-brand-secondary">{center.totalStudents}</div>
                <div className="text-xs text-gray-600">Étudiants</div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-brand-mint">{center.certifications.length}</div>
                <div className="text-xs text-gray-600">Certifications</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-brand-primary/5">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm pt-16 sm:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="text-center mb-4 sm:mb-6 lg:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent mb-2 sm:mb-3 lg:mb-4">
              {t('home.trainingCenters.title')}
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              {t('home.trainingCenters.subtitle')}
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-4 sm:mb-6 lg:mb-8 px-2 sm:px-0">
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
              <input
                type="text"
                placeholder={t('home.trainingCenters.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 border-2 border-brand-primary/20 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 text-sm sm:text-base lg:text-lg"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          {/* Sidebar Filters */}
          <div className="hidden lg:block w-full lg:w-80 flex-shrink-0">
            <div className="space-y-6">
              {/* Filter Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{t('home.trainingCenters.filters')}</h2>
                <div className="flex items-center gap-2">
                  {getActiveFiltersCount() > 0 && (
                    <button
                      onClick={resetFilters}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      <FilterX className="h-4 w-4 inline mr-1" />
                      {t('home.trainingCenters.clearFilters')}
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-brand-surface via-white to-brand-primary/5 rounded-2xl p-6 border border-brand-primary/10 shadow-lg">
                <div className="space-y-6">
                  {/* Country Filter */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-brand-primary/10 to-brand-primary/20 rounded-lg">
                        <Globe className="h-5 w-5 text-brand-primary" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">{t('home.trainingCenters.country')}</h3>
                    </div>
                    <select
                      value={selectedCountry || ''}
                      onChange={(e) => {
                        const countryId = e.target.value ? parseInt(e.target.value) : null;
                        setSelectedCountry(countryId);
                        if (!countryId) {
                          setSelectedWilaya('');
                        }
                      }}
                      className="w-full border border-brand-primary/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 bg-white/80"
                    >
                      <option value="">{t('home.trainingCenters.selectCountry')}</option>
                      {countries.map(country => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Wilaya Filter - Only show when a country with wilayas is selected */}
                  {selectedCountry && wilayas.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-brand-primary/10 to-brand-primary/20 rounded-lg">
                          <MapPin className="h-5 w-5 text-brand-primary" />
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900">Wilaya</h3>
                      </div>
                      <select
                        value={selectedWilaya}
                        onChange={(e) => setSelectedWilaya(e.target.value)}
                      className="w-full border border-brand-primary/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 bg-white/80"
                    >
                      <option value="">Toutes les wilayas</option>
                      {wilayas.map(wilaya => (
                        <option key={wilaya.id} value={wilaya.name}>
                          {wilaya.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  )}

                  {/* Category Filter */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-brand-secondary/10 to-brand-secondary/20 rounded-lg">
                        <GraduationCap className="h-5 w-5 text-brand-secondary" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">{t('home.centers.category', 'Catégorie')}</h3>
                    </div>
                    <select
                      value={selectedSpecialty}
                      onChange={(e) => setSelectedSpecialty(e.target.value)}
                      className="w-full border border-brand-secondary/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 focus:border-brand-secondary transition-all duration-200 bg-white/80"
                    >
                      <option value="">{t('home.centers.allCategories', 'Toutes les catégories')}</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.name_french || category.name}>
                          {getLocalizedName(category)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Rating Filter */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-brand-accent/20 to-brand-accent/30 rounded-lg">
                        <Star className="h-5 w-5 text-brand-accent" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">Note minimum</h3>
                    </div>
                    <select
                      value={minRating}
                      onChange={(e) => setMinRating(parseFloat(e.target.value))}
                      className="w-full border border-brand-accent/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-200 bg-white/80"
                    >
                      <option value={0}>Toutes les notes</option>
                      <option value={4}>4+ étoiles</option>
                      <option value={4.5}>4.5+ étoiles</option>
                    </select>
                  </div>

                  {/* Verified Filter */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-brand-mint/20 to-brand-mint/30 rounded-lg">
                        <Shield className="h-5 w-5 text-brand-mint" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">Statut</h3>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={showVerifiedOnly}
                          onChange={(e) => setShowVerifiedOnly(e.target.checked)}
                          className="rounded border-brand-primary/20 text-brand-primary focus:ring-brand-primary/20"
                        />
                        <span className="text-sm text-gray-700">Centres vérifiés uniquement</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={showPremiumOnly}
                          onChange={(e) => setShowPremiumOnly(e.target.checked)}
                          className="rounded border-brand-secondary/20 text-brand-secondary focus:ring-brand-secondary/20"
                        />
                        <span className="text-sm text-gray-700">Centres premium uniquement</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-md hover:shadow-lg"
              >
                <Filter className="h-4 w-4" />
                Filtres ({getActiveFiltersCount()})
              </button>
            </div>

            {/* Mobile Filter Drawer */}
            {showMobileFilters && (
              <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileFilters(false)}>
                <motion.div
                  initial={{ x: '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-100%' }}
                  className="absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-gray-900">Filtres</h2>
                      <button
                        onClick={() => setShowMobileFilters(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="space-y-4">
                      {/* Country Filter */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-brand-primary" />
                          <h3 className="text-sm font-semibold text-gray-900">{t('home.trainingCenters.country')}</h3>
                        </div>
                        <select
                          value={selectedCountry || ''}
                          onChange={(e) => {
                            const countryId = e.target.value ? parseInt(e.target.value) : null;
                            setSelectedCountry(countryId);
                            if (!countryId) {
                              setSelectedWilaya('');
                            }
                          }}
                          className="w-full border border-brand-primary/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary bg-white"
                        >
                          <option value="">{t('home.trainingCenters.selectCountry')}</option>
                          {countries.map(country => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Wilaya Filter */}
                      {selectedCountry && wilayas.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-brand-primary" />
                            <h3 className="text-sm font-semibold text-gray-900">{t('home.trainingCenters.wilaya')}</h3>
                          </div>
                          <select
                            value={selectedWilaya}
                            onChange={(e) => setSelectedWilaya(e.target.value)}
                            className="w-full border border-brand-primary/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary bg-white"
                          >
                            <option value="">{t('home.trainingCenters.allWilayas')}</option>
                            {wilayas.map(wilaya => (
                              <option key={wilaya.id} value={wilaya.name}>
                                {wilaya.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Category Filter */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <GraduationCap className="h-4 w-4 text-brand-secondary" />
                          <h3 className="text-sm font-semibold text-gray-900">{t('home.trainingCenters.category')}</h3>
                        </div>
                        <select
                          value={selectedSpecialty}
                          onChange={(e) => setSelectedSpecialty(e.target.value)}
                          className="w-full border border-brand-secondary/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 focus:border-brand-secondary bg-white"
                        >
                          <option value="">{t('home.trainingCenters.allCategories')}</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.name_french || category.name}>
                              {getLocalizedName(category)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Verified Filter */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-brand-mint" />
                          <h3 className="text-sm font-semibold text-gray-900">Statut</h3>
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                            <input
                              type="checkbox"
                              checked={showVerifiedOnly}
                              onChange={(e) => setShowVerifiedOnly(e.target.checked)}
                              className="rounded border-brand-primary/20 text-brand-primary focus:ring-brand-primary/20"
                            />
                            <span className="text-sm text-gray-700">Centres vérifiés uniquement</span>
                          </label>
                          <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                            <input
                              type="checkbox"
                              checked={showPremiumOnly}
                              onChange={(e) => setShowPremiumOnly(e.target.checked)}
                              className="rounded border-brand-secondary/20 text-brand-secondary focus:ring-brand-secondary/20"
                            />
                            <span className="text-sm text-gray-700">Centres premium uniquement</span>
                          </label>
                        </div>
                      </div>

                      {/* Clear Filters */}
                      {getActiveFiltersCount() > 0 && (
                        <button
                          onClick={resetFilters}
                          className="w-full text-sm text-red-600 hover:text-red-700 font-medium py-2 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          <FilterX className="h-4 w-4 inline mr-1" />
                          {t('home.trainingCenters.clearFilters')}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {pagination.total} {t('public.trainingCenters.resultsFound')}
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mt-1">
                  {getActiveFiltersCount() > 0 && `${getActiveFiltersCount()} filtre(s) actif(s)`}
                </p>
              </div>

              <div className="flex items-center gap-2 sm:gap-4">
                {/* Sort Dropdown */}
                <div className="relative flex-1 sm:flex-none">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full sm:w-auto appearance-none bg-white border border-brand-primary/20 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 pr-8 text-xs sm:text-sm focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 min-w-[120px] sm:min-w-[140px]"
                  >
                    <option value="rating">Mieux notés</option>
                    <option value="name">Nom A-Z</option>
                    <option value="students">Plus d'étudiants</option>
                    <option value="trainings">{t('public.trainingCenters.sortBy.trainings')}</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400 pointer-events-none" />
                </div>

                {/* View Mode Toggle */}
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 sm:p-2.5 ${viewMode === 'grid' ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white' : 'bg-white text-gray-600 hover:bg-brand-primary/5'}`}
                  >
                    <Building2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 sm:p-2.5 ${viewMode === 'list' ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white' : 'bg-white text-gray-600 hover:bg-brand-primary/5'}`}
                  >
                    <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
              </div>
            ) : filteredCenters.length > 0 ? (
              <div className="space-y-6">
                {filteredCenters.map((center, index) => (
                  <TrainingCenterItem key={center.id} center={center} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="p-3 sm:p-4 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                  <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-brand-primary" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  {t('public.trainingCenters.noResults')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-md mx-auto px-2">
                  {t('public.trainingCenters.noResultsDesc')}
                </p>
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-brand-primary to-brand-secondary text-white text-sm sm:text-base font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-lg"
                >
                  <FilterX className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                  Effacer tous les filtres
                </button>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-8 sm:mt-10">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-br from-brand-surface via-white to-brand-primary/5 rounded-2xl p-4 sm:p-6 border border-brand-primary/10 shadow-lg">
                  {/* Page Info */}
                  <div className="text-sm sm:text-base text-gray-600 font-medium">
                    {t('home.trainingCenters.pageInfo', {
                      current: pagination.page,
                      total: pagination.totalPages,
                      count: filteredCenters.length,
                      totalCount: pagination.total
                    })}
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => {
                        setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }));
                      }}
                      disabled={pagination.page === 1}
                      className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-brand-primary/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md disabled:hover:from-brand-primary disabled:hover:to-brand-secondary"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">{t('home.trainingCenters.previous')}</span>
                      <span className="sm:hidden">Préc.</span>
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      {(() => {
                        const pages: (number | string)[] = [];
                        const totalPages = pagination.totalPages;
                        const currentPage = pagination.page;
                        const maxVisible = 5;

                        if (totalPages <= maxVisible) {
                          // Show all pages if total is less than max visible
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Smart pagination: show first, last, current, and neighbors
                          if (currentPage <= 3) {
                            // Near the start
                            for (let i = 1; i <= 4; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          } else if (currentPage >= totalPages - 2) {
                            // Near the end
                            pages.push(1);
                            pages.push('ellipsis');
                            for (let i = totalPages - 3; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // In the middle
                            pages.push(1);
                            pages.push('ellipsis');
                            for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === 'ellipsis') {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className="px-2 text-gray-400 font-medium"
                              >
                                ...
                              </span>
                            );
                          }

                          const pageNum = page as number;
                          const isActive = pageNum === currentPage;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setPagination(prev => ({ ...prev, page: pageNum }));
                              }}
                              className={`min-w-[40px] sm:min-w-[44px] h-10 sm:h-11 px-3 sm:px-4 text-sm sm:text-base font-semibold rounded-xl transition-all duration-200 ${
                                isActive
                                  ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-lg shadow-brand-primary/30 scale-105'
                                  : 'text-gray-700 bg-white border-2 border-brand-primary/20 hover:border-brand-primary/40 hover:bg-gradient-to-br hover:from-brand-primary/5 hover:to-brand-secondary/5 hover:shadow-md'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => {
                        setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }));
                      }}
                      disabled={pagination.page === pagination.totalPages}
                      className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-brand-primary/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md disabled:hover:from-brand-primary disabled:hover:to-brand-secondary"
                    >
                      <span className="hidden sm:inline">{t('home.trainingCenters.next')}</span>
                      <span className="sm:hidden">Suiv.</span>
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingCentersPage;
